import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { find } from 'lodash';
import MatrixVertical from './MatrixVertical';
import MatrixHorizontal from './MatrixHorizontal';
import { SIMPLE_MATRIX_APPEARANCE_STYLES } from '../../../constants';
import { removeScorepointFromAnswerArray } from './utils';

function getScorepointsToRemove($elements) {
  const scorepointIdsToRemove = [];
  $elements.each(function removeScorepoint() {
    const scorepointId = $(this).attr('data-scorepoint-id');
    scorepointIdsToRemove.push(scorepointId);
  });
  return scorepointIdsToRemove;
}

export default function Matrix({
  question = {},
  answers = [],
  scorepoints = [],
  getText,
}) {
  const { matrixStyle } = question;
  const [selectedScorepoints, setScorepoints] = useState([]);
  const onHandleMatrixAnswerTap = useCallback(
    ({ target }) => {
      let newSelectedScorepoints = [...selectedScorepoints];
      const selectedScorepointElement = $(target).closest('.matrix__answer');
      const selectedScorepointId = selectedScorepointElement.data(
        'scorepoint-id',
      );
      const answerId = selectedScorepointElement.attr('data-answer-id');
      const answerElement = $(`.answerMatrix[data-answer-id=${answerId}]`);
      const answersContainer = answerElement.find('.matrix__answers');

      const selectedLabel = answerElement.find(
        `.matrix__selected-answer[data-scorepoint-id=${selectedScorepointId}]`,
      );

      const scorepointIdsToRemove = [];
      // we can select only one exclusive answer per question
      // this feature is allowed only in multiple select
      if (
        selectedScorepointElement.hasClass('exclusive-answer') ||
        selectedScorepointElement.hasClass('none-of-the-above')
      ) {
        // unselect selected answers
        const $selectedSiblings = selectedScorepointElement.siblings(
          '.answer--click',
        );
        scorepointIdsToRemove.push(
          ...getScorepointsToRemove($selectedSiblings),
        );
      } else if (selectedScorepointElement.hasClass('answer--multi-choice')) {
        const $selectedExclusiveSiblings = selectedScorepointElement.siblings(
          '.exclusive-answer.answer--click',
        );
        const $selectedNoneOfTheAboveSibling = selectedScorepointElement.siblings(
          '.none-of-the-above.answer--click',
        );

        scorepointIdsToRemove.push(
          ...getScorepointsToRemove($selectedExclusiveSiblings),
        );
        if ($selectedNoneOfTheAboveSibling.length > 0) {
          scorepointIdsToRemove.push(
            ...getScorepointsToRemove($selectedNoneOfTheAboveSibling),
          );
        }
      }

      scorepointIdsToRemove.forEach((scorepointId) => {
        newSelectedScorepoints = removeScorepointFromAnswerArray(
          scorepointId,
          newSelectedScorepoints,
          answerId,
        );
      });

      if (selectedScorepointElement.hasClass('answer--multi-choice')) {
        selectedScorepointElement.toggleClass('answer--click');
        selectedLabel.toggleClass('matrix__selected-answer--active');

        if (selectedScorepointElement.hasClass('answer--click')) {
          const selectedAnswer = find(newSelectedScorepoints, [
            'answerId',
            answerId,
          ]);
          if (!selectedAnswer) {
            newSelectedScorepoints.push({
              answerId,
              scorepointIds: [selectedScorepointId],
            });
          } else {
            selectedAnswer.scorepointIds.push(selectedScorepointId);
          }
        } else {
          newSelectedScorepoints = removeScorepointFromAnswerArray(
            selectedScorepointId,
            newSelectedScorepoints,
            answerId,
          );
        }

        const answer = find(newSelectedScorepoints, ['answerId', answerId]);
        const selectedScorepointIds = answer.scorepointIds;
        answerElement.attr(
          'data-selected-scorepoint-id',
          selectedScorepointIds.join(','),
        );
      } else {
        selectedScorepointElement.siblings().removeClass('answer--click');
        selectedScorepointElement.addClass('answer--click');
        answerElement.attr('data-selected-scorepoint-id', selectedScorepointId);
        answerElement
          .find('.matrix__labels span')
          .removeClass('matrix__selected-answer--active');
        selectedLabel.addClass('matrix__selected-answer--active');
        answersContainer.addClass('matrix__answers--closed').slideUp();
      }
      setScorepoints(newSelectedScorepoints);
      Pollfish.ui.validateQuestionAnswers(
        Pollfish.ui.getClosestQuestionWrapperElement(selectedScorepointElement),
      );
    },
    [selectedScorepoints, setScorepoints],
  );
  const onHandleMatrixQuestionTap = useCallback((e) => {
    const { target: selectedQuestion } = e;
    const target = $(selectedQuestion)
      .parents('.answerMatrix')
      .attr('data-answer-id');
    const answersDiv = $(`.matrix__answers[data-answer-id=${target}]`);
    const matrix = $(`.matrix[data-answer-id=${target}]`);

    if (answersDiv.hasClass('matrix__answers--closed')) {
      answersDiv.removeClass('matrix__answers--closed').slideDown();
      matrix.removeClass('matrix--closed');
    } else {
      answersDiv.addClass('matrix__answers--closed').slideUp();
      matrix.addClass('matrix--closed');
    }
  }, []);
  const onHandleMatrixHideAnswersButton = useCallback((e) => {
    e.preventDefault();
    const answerId = $(e.target).attr('data-answer-id');

    $(`.matrix__answers[data-answer-id=${answerId}]`)
      .addClass('matrix__answers--closed')
      .slideUp();
    $(`.matrix[data-answer-id=${answerId}]`).addClass('matrix--closed');
  }, []);

  switch (matrixStyle) {
    case SIMPLE_MATRIX_APPEARANCE_STYLES.HORIZONTAL:
      return (
        <MatrixHorizontal
          question={question}
          rows={answers}
          columns={scorepoints}
          onHandleMatrixQuestionTap={onHandleMatrixQuestionTap}
          onHandleMatrixAnswerTap={onHandleMatrixAnswerTap}
        />
      );
    case SIMPLE_MATRIX_APPEARANCE_STYLES.VERTICAL:
    default:
      return (
        <MatrixVertical
          question={question}
          rows={answers}
          columns={scorepoints}
          onHandleMatrixQuestionTap={onHandleMatrixQuestionTap}
          onHandleMatrixAnswerTap={onHandleMatrixAnswerTap}
          onHandleMatrixHideAnswersButton={onHandleMatrixHideAnswersButton}
          getText={getText}
        />
      );
  }
}

Matrix.propTypes = {
  question: PropTypes.object,
  answers: PropTypes.array,
  scorepoints: PropTypes.array,
  getText: PropTypes.func.isRequired,
};
